import { t } from '@lingui/macro'
import { StyleSheet, Text, View } from '@react-pdf/renderer'
import { dateFormat } from 'app/appSettings'
import { pdfDefaultFontSize } from 'app/views/forms/pdf-components/PDFCommon'
import { pdfTableStyles } from 'app/views/forms/pdf-components/PdfTableStyles'
import { PdfSplittableTableRow } from 'app/views/forms/pdf-components/PdfSplittableTableRow'
import { PdfTableCell } from 'app/views/forms/pdf-components/PdfTableCell'
import { PdfTableHeaderCell } from 'app/views/forms/pdf-components/PdfTableHeaderCell'
import { PdfUnsplittableTableRow } from 'app/views/forms/pdf-components/PdfUnsplittableTableRow'

import moment from 'moment'
import { myI18n } from 'translation/I18nConnectedProvider'

// Same layout as the milestones table which this element updates
const styles = StyleSheet.create({
  stageCol: {
    width: '10%',
    fontWeight: 'bold'
  },
  dateCol: {
    width: '10%'
  },
  textCol: {
    width: '35%'
  }
})

/**
 * Form element which allows to display milestone updates in PDF.
 * @category Form
 * @subcategory Form elements
 * @component
 * @returns {JSX.Element}
 * @param  {Object} typeProps - Element specific properties that can be configured in form editor.
 * @param  {SalesforceObjectConnection} connectedObject Connected Salesforce object of type Opportunity.
 */
export const FormMilestoneUpdatePdf = ({
  id,
  title,
  helpText,
  connectedObject,
  value,
  langVersion,
  ...props
}) => {
  const milestones = value || []

  const areDatesChanged = milestones.some(
    (item) => item.milestoneUpdate.areDatesChanged === 'yes'
  )
    ? 'yes'
    : 'no'

  return (
    <View>
      <View>
        <Text style={pdfTableStyles.elementTitle}>
          {myI18n?._(t`FORM_MILESTONE_UPDATE_ARE_DATES_CHANGED_SUBTITLE`)}:
        </Text>
      </View>

      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          marginBottom: 8
        }}
      >
        <Text
          style={{
            fontSize: pdfDefaultFontSize,
            lineHeight: 1.4,
            marginRight: 20,
            marginTop: 4
          }}
        >
          {areDatesChanged === 'yes' ? myI18n?._(t`Yes`) : myI18n?._(t`No`)}
        </Text>
      </View>

      <View style={pdfTableStyles.table}>
        <PdfUnsplittableTableRow>
          <PdfTableHeaderCell style={styles.stageCol}>
            {myI18n?._(t`Stage`)}
          </PdfTableHeaderCell>
          <PdfTableHeaderCell style={styles.dateCol}>
            {myI18n?._(t`Start date`)}
          </PdfTableHeaderCell>
          <PdfTableHeaderCell style={styles.dateCol}>
            {myI18n?._(t`End date`)}
          </PdfTableHeaderCell>
          <PdfTableHeaderCell style={styles.textCol}>
            {myI18n?._(t`MILESTONES_UPDATE_PDF_PLANNED_ACTIVITIES_LABEL`)}
          </PdfTableHeaderCell>
          <PdfTableHeaderCell style={styles.textCol}>
            {myI18n?._(t`MILESTONES_UPDATE_PDF_CARRIED_OUT_ACTIVITIES_LABEL`)}
          </PdfTableHeaderCell>
        </PdfUnsplittableTableRow>

        {milestones.map((item, index) => {
          const startDate = item?.milestoneUpdate?.phaseStartDate
          const endDate = item?.milestoneUpdate?.phaseEndDate

          return (
            <PdfSplittableTableRow key={index}>
              <PdfTableCell style={styles.stageCol}>
                <Text>{myI18n?._(item.stage)}</Text>
              </PdfTableCell>
              <PdfTableCell style={styles.dateCol}>
                <Text>{startDate ? moment.utc(startDate).format(dateFormat) : ''}</Text>
              </PdfTableCell>
              <PdfTableCell style={styles.dateCol}>
                <Text>{endDate ? moment.utc(endDate).format(dateFormat) : ''}</Text>
              </PdfTableCell>
              <PdfTableCell style={styles.textCol}>
                <Text>{item?.milestoneUpdate?.primaryActivities || ''}</Text>
              </PdfTableCell>
              <PdfTableCell style={styles.textCol}>
                <Text>{item?.milestoneUpdate?.implementedActivities || ''}</Text>
              </PdfTableCell>
            </PdfSplittableTableRow>
          )
        })}
      </View>
    </View>
  )
}
